import React from 'react';
import { formatSpeed, formatLatency } from '../utils/statistics';
import type { DiagnosticResult } from '../types/diagnostics';

interface Props {
  result: DiagnosticResult;
}

const gradeColors: Record<string, string> = {
  A: 'text-emerald-400',
  B: 'text-blue-400',
  C: 'text-yellow-400',
  D: 'text-orange-400',
  F: 'text-red-400',
};

const gradeLabels: Record<string, string> = {
  A: 'Excellent',
  B: 'Good',
  C: 'Fair',
  D: 'Poor',
  F: 'Bad',
};

export const QualityScore: React.FC<Props> = ({ result }) => {
  const quality = result.quality;
  if (!quality) return null;

  const grade = quality.overallGrade;
  const mos = quality.mosScore;
  const circumference = 2 * Math.PI * 45;
  const mosPercent = (mos - 1) / 4;
  const offset = circumference * (1 - mosPercent);

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
        Quality Score
      </h3>

      <div className="flex items-center gap-8">
        {/* MOS Gauge */}
        <div className="relative w-32 h-32 flex-shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle
              cx="50" cy="50" r="45"
              fill="none"
              stroke="#1e293b"
              strokeWidth="8"
            />
            <circle
              cx="50" cy="50" r="45"
              fill="none"
              stroke={mos >= 4 ? '#22c55e' : mos >= 3 ? '#f59e0b' : '#ef4444'}
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
              style={{ animation: 'gauge-fill 1.5s ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${gradeColors[grade]}`}>{grade}</span>
            <span className="text-xs text-slate-400">MOS {mos.toFixed(1)}</span>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex-1 grid grid-cols-2 gap-3">
          <MetricItem
            label="Grade"
            value={gradeLabels[grade]}
            color={gradeColors[grade]}
          />
          <MetricItem
            label="Download"
            value={result.download ? formatSpeed(result.download.speedMbps) : '—'}
          />
          <MetricItem
            label="Upload"
            value={result.upload ? formatSpeed(result.upload.speedMbps) : '—'}
          />
          <MetricItem
            label="Latency"
            value={result.ping ? formatLatency(result.ping.avg) : '—'}
          />
          <MetricItem
            label="Jitter"
            value={result.ping ? formatLatency(result.ping.jitter) : '—'}
          />
          <MetricItem
            label="Loss"
            value={result.ping ? `${result.ping.lossPercent}%` : '—'}
          />
          <MetricItem
            label="Flow Stability"
            value={`${Math.round(quality.flowStability * 100)}%`}
          />
          <MetricItem
            label="Buffer Health"
            value={`${Math.round(quality.bufferHealth * 100)}%`}
          />
        </div>
      </div>

      {/* Alerts */}
      <div className="mt-4 space-y-2">
        {quality.congestionProbability > 0.3 && (
          <Alert type="warning" text={`Congestion detected (${Math.round(quality.congestionProbability * 100)}% probability)`} />
        )}
        {quality.ispShapingDetected && (
          <Alert type="danger" text="ISP traffic shaping detected" />
        )}
        {result.streamDiagnostics?.holBlockingDetected && (
          <Alert type="warning" text="Head-of-line blocking detected" />
        )}
      </div>
    </div>
  );
};

const MetricItem: React.FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="flex flex-col">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm font-medium ${color ?? 'text-white'}`}>{value}</span>
  </div>
);

const Alert: React.FC<{ type: 'warning' | 'danger'; text: string }> = ({ type, text }) => (
  <div
    className={`px-3 py-2 rounded-lg text-xs font-medium ${
      type === 'warning'
        ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
        : 'bg-red-500/10 text-red-400 border border-red-500/20'
    }`}
  >
    {text}
  </div>
);
